import {BulkAddQueryValidation} from '../strings'

/**
 * Extracts qualifier:value pairs from a search query string.
 * Handles quoted values and preserves the original formatting.
 *
 * @param input - The search query string
 * @returns Array of qualifier:value pairs found in the query
 */
const extractQualifierPairs = (input: string): Array<string> => {
  const pairs: Array<string> = []

  // Match qualifier:value patterns where value can be:
  // - A quoted string (single or double quotes, with escaped quotes inside)
  // - An unquoted word
  const qualifierRegex = /(\w+):("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|\S+)/g

  let match
  while ((match = qualifierRegex.exec(input)) !== null) {
    pairs.push(match[0]) // The full qualifier:value pair
  }

  return pairs
}

type Qualifier = {
  term: string
  description: string
  scope: 'narrow' | 'broad'
}

const SCOPE_QUALIFIERS: Array<Qualifier> = [
  {term: 'repo', description: 'a repository (repo:owner/name)', scope: 'narrow'},
  {term: 'org', description: 'an organization (org:organization-name)', scope: 'broad'},
  {term: 'user', description: 'a user (user:username)', scope: 'broad'},
]

export interface ValidationEnvironment {
  isEnterpriseRuntime: boolean
  isMultiTenantEnterprise: boolean
}

export interface ValidationResult {
  isValid: boolean
  error?: string
}

/**
 * Validates bulk add search input for basic correctness and enterprise security considerations.
 *
 * Enterprise Security Analysis:
 * Based on investigation of GitHub's backend search infrastructure, cross-repository
 * search is supported in all environments including GHES and Proxima. However,
 * enterprise environments have permission-based access restrictions that require
 * specific repository scoping rather than broad organizational queries.
 *
 * Security Model:
 * - Enterprise users can search across repositories they have permission to access
 * - Broad org:/user: qualifiers may return repositories outside permission scope
 * - Backend ConditionalIssueQuery validates access per repository, not per query type
 * - Permission validation occurs at the security layer, not query validation layer
 *
 * Validation Approach:
 * - In enterprise environments: Allow cross-repo searches with explicit repo: scoping,
 *   but prevent broad org:/user: qualifiers without specific repository context
 * - In GitHub.com: Allow broad org:/user: qualifiers (standard behavior)
 * - All environments: Require some form of scoping qualifier
 */
export const validateBulkAddQuery = (input: string, environment: ValidationEnvironment): ValidationResult => {
  if (!input.trim()) {
    return {isValid: true} // Allow empty queries
  }

  const qualifierPairs = extractQualifierPairs(input)
  const isEnterpriseEnvironment = environment.isEnterpriseRuntime || environment.isMultiTenantEnterprise

  // Enterprise environments: require explicit repository scoping for security
  if (isEnterpriseEnvironment) {
    const narrowScopeQualifiers = SCOPE_QUALIFIERS.filter(q => q.scope === 'narrow').map(({term}) => term.toLowerCase())
    const broadScopeQualifiers = SCOPE_QUALIFIERS.filter(q => q.scope === 'broad').map(({term}) => term.toLowerCase())

    const hasNarrowQualifier = qualifierPairs.some(pair => {
      const qualifier = pair.split(':')[0]?.toLowerCase()
      return qualifier && narrowScopeQualifiers.includes(qualifier)
    })

    const hasOnlyBroadQualifiers =
      qualifierPairs.some(pair => {
        const qualifier = pair.split(':')[0]?.toLowerCase()
        return qualifier && broadScopeQualifiers.includes(qualifier)
      }) && !hasNarrowQualifier

    if (hasOnlyBroadQualifiers) {
      return {
        isValid: false,
        error: BulkAddQueryValidation.enterpriseRequiresExplicitRepoScoping,
      }
    }
  } else {
    // Extract scope qualifier names from SCOPE_QUALIFIERS
    const scopeQualifierNames = SCOPE_QUALIFIERS.map(({term}) => term.toLowerCase())

    // Check if we have any scope qualifiers (repo:, org:, user:)
    const hasScopeQualifiers = qualifierPairs.some(pair => {
      const qualifier = pair.split(':')[0]?.toLowerCase()
      return qualifier && scopeQualifierNames.includes(qualifier)
    })

    if (!hasScopeQualifiers) {
      return {
        isValid: false,
        error: BulkAddQueryValidation.requiredAdvancedSearchArgs(SCOPE_QUALIFIERS),
      }
    }
  }

  return {isValid: true}
}

// Export qualifiers for testing
export {extractQualifierPairs, SCOPE_QUALIFIERS}
